import { Dispatch, SetStateAction } from 'react';

import { Center, Spinner } from '@chakra-ui/react';

import { ColumnDef, PaginationState } from '@tanstack/react-table';

import EmptyState from 'app/components/UI/EmptyState';
import { Table } from 'app/components/UI/Table/Table';

import { ListPage } from 'app/types/reconciliations';

export const RECONCILIATION_PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];

interface ReconciliationPagedTableProps<T extends object> {
  page?: ListPage<T>;
  isLoading: boolean;
  isError: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  getRowId: (row: T) => string;
  emptyText: string;
  errorText: string;
  testId: string;
}

/** Shows one page of a reconciliation drill-down list, with its loading, empty and error states. */
function ReconciliationPagedTable<T extends object>({
  page = undefined,
  isLoading,
  isError,
  columns,
  pagination,
  onPaginationChange,
  getRowId,
  emptyText,
  errorText,
  testId,
}: ReconciliationPagedTableProps<T>) {
  if (isLoading) {
    return (
      <Center w="full" py="40px" data-testid={`${testId}-loading`}>
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  if (isError) {
    return <EmptyState text={errorText} />;
  }

  if (!page || page.list.length === 0) {
    return <EmptyState text={emptyText} />;
  }

  return (
    <Table
      columns={columns}
      data={page.list}
      total={page.total}
      pageNumber={pagination.pageIndex}
      pageSize={pagination.pageSize}
      pageSizeOptions={RECONCILIATION_PAGE_SIZE_OPTIONS}
      onPaginationChange={onPaginationChange}
      getRowId={getRowId}
    />
  );
}

export default ReconciliationPagedTable;
